import React, { useState } from "react";
import { ArrowRightLeft, KanbanSquare, Loader2, X } from "lucide-react";
import { PLACEMENTS } from "../../leads/components/AddSellerLeadModal";

// Where Database rows land on Prospects (Genneva Smarrelli, CRM-2026-00121,
// 21 Oct 2026: "under prospects there is no way that I can currently move a
// property from database to my pipeline"). The same New / Cold / Warm / Hot
// buttons as the Add Lead form, plus Long Term, which is where the old
// "Move to Long Term" button put everything, and Appraisal (Christian Gravias,
// Monday 13343075874, 24 Oct 2026: "Please add Appraisal to this tile also"),
// in the Prospects Appraisal column's amber.
export const PIPELINE_PLACEMENTS = [
  ...PLACEMENTS,
  { value: "long_term", label: "Long Term", on: "bg-[#c026d3] text-white border-[#c026d3]" },
  { value: "appraisal", label: "Appraisal", on: "bg-[#d99018] text-white border-[#d99018]" },
];

export const PIPELINE_HINTS = {
  new: "Prospects > New, not called yet.",
  cold: "Prospects > Connected, marked as called, Cold.",
  warm: "Prospects > Connected, marked as called, Warm.",
  hot: "Prospects > Connected, marked as called, Hot.",
  long_term: "Prospects > Long Term, not selling soon, keep in touch.",
  appraisal: "Prospects > Appraisal, on the Off-Market Pipeline.",
};

/**
 * Pick the Prospects column for the selected owner rows. The rows stay in the
 * database too; the Database is still the agent's calling list.
 *
 * `appraisalOpensForm`: one of the agent's own rows goes to Appraisal through
 * the Add Appraisal form, address filled in, like the row's Convert button
 * (Christian Gravias, 2026-08-03: open the form, not a stub behind their back).
 * Several rows, or a colleague's row, become the appraisal straight away.
 */
export default function MoveToPipelineModal({
  count, agentName, defaultPlacement = "long_term", appraisalOpensForm = false, onClose, onConfirm,
}) {
  const [placement, setPlacement] = useState(
    PIPELINE_PLACEMENTS.some((p) => p.value === defaultPlacement) ? defaultPlacement : "long_term"
  );
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      if (await onConfirm(placement)) onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[540px] max-w-full flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">
            {/* A colleague's Database: the lead goes on their board, not yours. */}
            Move {count} {count === 1 ? "owner" : "owners"} to {agentName ? `${agentName}'s` : "your"} Pipeline
          </h3>
          <button onClick={onClose} className="text-gray-500" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-2">
          <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold" id="pipeline-placement">
            Where should {count === 1 ? "it" : "they"} sit?
          </span>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" role="radiogroup" aria-labelledby="pipeline-placement">
            {PIPELINE_PLACEMENTS.map((p) => {
              const on = placement === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setPlacement(p.value)}
                  className={`py-2 rounded-lg border text-sm font-semibold transition-colors ${
                    on ? p.on : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-500">
            {placement === "appraisal" && appraisalOpensForm
              ? "Opens the Appraisal form with this address filled in. Once saved it sits in Prospects > Appraisal."
              : PIPELINE_HINTS[placement]}
          </p>
          <p className="text-[11px] text-gray-400">
            {count === 1 ? "The row stays" : "The rows stay"} in the database too
            {placement === "appraisal" ? ", flagged Appraisal." : "."}
          </p>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-[#55c5d0] hover:bg-[#45b5c0] disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : placement === "appraisal" ? (
              <ArrowRightLeft size={14} />
            ) : (
              <KanbanSquare size={14} />
            )}
            {placement === "appraisal" && appraisalOpensForm ? "Open Appraisal form" : "Move to Pipeline"}
          </button>
        </div>
      </div>
    </div>
  );
}
